import type { AvatarConfig, WordDTO } from "@shared/types";

export const QUIZ_PROTOCOL_VERSION = 1;

export type QuizPhase = "lobby" | "question" | "reveal" | "leaderboard" | "podium";

export interface QuizPlayer {
  id: string;
  displayName: string;
  avatarConfig: AvatarConfig;
  score: number;
  /** Points won on the most recent question (for the +N pop). */
  lastDelta: number;
  answered: boolean;
}

export interface QuizSettings {
  /** Word packs to draw questions from (combined pool). */
  packIds: string[];
  /** Seconds allowed per question. */
  timeLimitSec: number;
  /** How many questions in the game. */
  questionCount: number;
  /** Enabled question-type ids (subset of QUIZ_QUESTION_TYPES). */
  enabledTypes: string[];
}

export interface QuizRoomState {
  code: string;
  /** Presenter (host display) session id — not a player. */
  hostId: string;
  phase: QuizPhase;
  players: QuizPlayer[];
  settings: QuizSettings;
  seed: number;
  questionIndex: number;
  questionStartedAt?: number;
  /** Resolved word pool (sent by the host on start) so every device builds
   *  the SAME deterministic questions even for custom packs. */
  pool?: WordDTO[];
  rev?: number;
  maxPlayers?: number;
}

export type QuizEvent =
  | { type: "state"; state: QuizRoomState }
  | { type: "error"; message: string };

export interface QuizJoinOpts {
  code: string;
  role: "host" | "controller";
  me: { id: string; displayName: string; avatarConfig: AvatarConfig };
  settings?: Partial<QuizSettings>;
}

export interface QuizClient {
  join(opts: QuizJoinOpts): Promise<QuizRoomState>;
  /** Host only: update room settings while in the lobby. */
  setSettings(settings: Partial<QuizSettings>): void;
  /** Host only: begin the quiz with the resolved word pool. */
  start(pool: WordDTO[]): void;
  /** Controller: submit the answer to the current question. */
  answer(correct: boolean, elapsedMs: number): void;
  /** Host only: skip ahead (question → reveal, or reveal → next). */
  next(): void;
  /** Host only: bring everyone back to the lobby (e.g. after the podium)
   *  with the same players — nobody is disconnected. */
  toLobby(): void;
  on(cb: (e: QuizEvent) => void): () => void;
  leave(): void;
}

/** Selectable question types (all answers are VISUAL — audio is only ever the
 *  host prompt, never an answer option). */
export const QUIZ_QUESTION_TYPES: { id: string; label: string; icon: string }[] = [
  { id: "audio-to-image", label: "Listen → Picture", icon: "🔊" },
  { id: "audio-to-english", label: "Listen → Word", icon: "👂" },
  { id: "image-to-word", label: "Picture → Word", icon: "🖼️" },
  { id: "spell", label: "Spelling", icon: "🔤" },
];
